import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATUS_LABELS, STATUS_STEPS } from "@/lib/constants";

const inputCls =
  "w-full bg-[#0A1628] border border-[#2C4066] rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-[#51ADDA] placeholder:text-slate-600";
const labelCls = "block font-mono text-xs uppercase tracking-wider text-slate-300 mb-2";

const Timeline = ({ status }) => {
  const current = STATUS_STEPS.indexOf(status);
  return (
    <div data-testid="suivi-timeline" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
      {STATUS_STEPS.map((s, i) => {
        const done = current >= i;
        return (
          <div
            key={s}
            className={`p-3 rounded border text-center ${
              done ? "border-[#51ADDA]/50 bg-[#51ADDA]/10 text-[#51ADDA]" : "border-[#1C2C4E] text-slate-500"
            }`}
          >
            <span className="font-mono text-[10px] uppercase tracking-widest">{STATUS_LABELS[s]}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function Suivi() {
  const [params] = useSearchParams();
  const [reference, setReference] = useState(params.get("reference") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devis, setDevis] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api
      .get("/payments/config")
      .then((r) => setPaymentConfig(r.data))
      .catch(() => setPaymentConfig({ provider: null }));
  }, []);

  const search = useCallback(async (ref, em) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/devis/suivi", { params: { reference: ref, email: em } });
      setDevis(r.data);
    } catch (e) {
      setDevis(null);
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ref = params.get("reference");
    const em = params.get("email");
    if (ref && em) search(ref, em);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retour de Stripe : confirmer le paiement avant d'afficher le succès
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 10 && !cancelled; i++) {
        try {
          const r = await api.get(`/payments/status/${sessionId}`);
          if (r.data.payment_status === "paid") {
            toast.success("Paiement confirmé, merci !");
            const ref = params.get("reference");
            const em = params.get("email");
            if (ref && em) search(ref, em);
            return;
          }
        } catch {
          /* réessaie */
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (!cancelled) toast.info("Paiement en cours de confirmation...");
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payWithStripe = async () => {
    setPaying(true);
    try {
      const r = await api.post("/payments/checkout", {
        reference: devis.reference,
        email,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    search(reference, email);
  };

  return (
    <div className="bg-[#0A1628] text-[#e2e8f0] min-h-screen font-body antialiased">
      <nav className="border-b border-[#1C2C4E] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="suivi-brand-link" className="font-display tracking-wider text-lg sm:text-xl uppercase text-white whitespace-nowrap">
            NEOELEC AUTO
          </Link>
          <Link to="/" data-testid="suivi-back-link" className="text-xs uppercase tracking-[0.14em] text-slate-400 hover:text-white transition-colors">
            ← Retour à la boutique
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.14em] text-[#51ADDA] block mb-2">Suivi & Paiement</span>
          <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight text-white">Suivre ma demande</h1>
        </div>

        <form onSubmit={submit} data-testid="suivi-form" className="bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="suivi-ref" className={labelCls}>Référence (ex: KW-A1B2C3)</label>
            <input id="suivi-ref" data-testid="suivi-reference-input" type="text" required value={reference} onChange={(e) => setReference(e.target.value)} placeholder="KW-......" className={inputCls} />
          </div>
          <div>
            <label htmlFor="suivi-email" className={labelCls}>Email de la demande</label>
            <input id="suivi-email" data-testid="suivi-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.fr" className={inputCls} />
          </div>
          <button
            type="submit"
            data-testid="suivi-search-button"
            disabled={loading}
            className="bg-[#51ADDA] text-[#0A1628] font-display uppercase tracking-wider text-lg py-3 px-6 rounded hover:bg-white transition-colors font-bold disabled:opacity-50"
          >
            {loading ? "Recherche..." : "Rechercher"}
          </button>
        </form>

        {error && (
          <div data-testid="suivi-error-alert" className="p-4 rounded bg-red-400/10 border border-red-400/30 text-red-400 text-sm mb-8">{error}</div>
        )}

        {devis && (
          <div data-testid="suivi-status-card" className="bg-[#0F1D36] border border-[#1C2C4E] rounded-xl p-6 md:p-10 shadow-2xl">
            {devis.status === "annulee" ? (
              <div data-testid="suivi-cancelled-alert" className="p-4 rounded bg-red-400/10 border border-red-400/30 text-red-400 text-sm">
                Cette demande a été annulée. Contactez-nous pour plus d'informations.
              </div>
            ) : (
              <Timeline status={devis.status} />
            )}

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm mb-8">
              {[
                ["Référence", devis.reference],
                ["Calculateur", devis.ecu_reference],
                ["Véhicule", `${devis.vehicle} (${devis.year})`],
                ["Motorisation", `${devis.motorisation} — ${devis.energie}${devis.puissance ? ` — ${devis.puissance}` : ""}`],
                ["Prestation", devis.prestation],
                ["Envoyée le", new Date(devis.created_at).toLocaleString("fr-FR")],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[#1C2C4E] pb-2">
                  <span className="font-mono text-xs uppercase tracking-wider text-slate-500">{k}</span>
                  <span className="text-slate-200 text-right">{v}</span>
                </div>
              ))}
            </div>

            {devis.admin_note && (
              <div className="p-4 rounded bg-[#0A1628] border border-[#1C2C4E] text-sm text-slate-300 mb-8">
                <span className="font-mono text-xs uppercase tracking-wider text-[#51ADDA] block mb-1">Message de l'atelier</span>
                {devis.admin_note}
              </div>
            )}

            {devis.status === "devis_envoye" && devis.price != null && (
              <div className="border border-[#51ADDA]/30 rounded-xl p-6 bg-[#0A1628]">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-xs uppercase tracking-wider text-slate-400">Montant de votre devis</span>
                  <span data-testid="suivi-price-value" className="font-display text-4xl text-[#51ADDA]">
                    {devis.price.toFixed(2)} €
                  </span>
                </div>
                {paymentConfig?.provider === "stripe" ? (
                  <button
                    type="button"
                    data-testid="stripe-pay-button"
                    disabled={paying}
                    onClick={payWithStripe}
                    className="w-full bg-[#EF3340] text-white font-display uppercase tracking-wider text-xl py-4 rounded hover:bg-[#f87171] transition-colors font-bold disabled:opacity-50"
                  >
                    {paying ? "Redirection vers le paiement..." : `Payer ${devis.price.toFixed(2)} € par carte bancaire`}
                  </button>
                ) : (
                  <div data-testid="payment-pending-notice" className="p-4 rounded border border-dashed border-[#2C4066] text-slate-400 text-sm text-center">
                    Le paiement en ligne est en cours d'activation. Votre lien de paiement vous sera transmis avec le devis.
                  </div>
                )}
              </div>
            )}

            {(devis.status === "payee" || devis.status === "terminee") && (
              <div data-testid="suivi-paid-confirmation" className="p-4 rounded bg-emerald-400/10 border border-emerald-400/30 text-emerald-400 text-sm">
                Paiement reçu{devis.paid_at ? ` le ${new Date(devis.paid_at).toLocaleString("fr-FR")}` : ""}.{" "}
                {devis.status === "terminee"
                  ? "Votre calculateur a été traité, testé sur banc et renvoyé en suivi 24h."
                  : "Vous pouvez nous expédier votre calculateur à : NeoElec Auto, 7 Rue Mozart, 57270 Uckange — l'intervention démarre à réception."}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
